import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import {
  getCurrentAppLanguage,
  getLanguagePreference,
  setLanguagePreference,
} from "@/i18n";
import { languageSettingsStrings as strings } from "@/i18n/features/language-settings.i18n";
import {
  getEnabledLanguages,
  getLanguageDisplayName,
} from "@/i18n/languages";
import type { LanguagePreference } from "@/i18n/resolve-language";
import { useAuthStore } from "@/state/auth-store";

/**
 * Language picker. Only publicly enabled languages are listed — languages
 * that exist in the codebase but are still under review never show here.
 */
export default function LanguageSettings() {
  const c = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const authStatus = useAuthStore((state) => state.status);
  const setProfile = useAuthStore((state) => state.setProfile);
  const [preference, setPreference] = useState<LanguagePreference | null>(null);

  useEffect(() => {
    void getLanguagePreference().then(setPreference);
  }, []);

  async function select(next: LanguagePreference) {
    setPreference(next);
    await setLanguagePreference(next);
    if (authStatus === "authenticated") {
      void setProfile({ locale: getCurrentAppLanguage() }).catch(() => {});
    }
  }

  const options: { value: LanguagePreference; label: string }[] = [
    { value: "system", label: t(strings.systemDefault) },
    ...getEnabledLanguages().map((language) => ({
      value: language as LanguagePreference,
      label: getLanguageDisplayName(language),
    })),
  ];

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.goBack)}
          onPress={() => router.back()}
          hitSlop={10}
          style={({ pressed }) => [styles.backButton, pressed && styles.pressed]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>

        <ThemedText variant="bodyStrong" style={styles.headerTitle}>
          {t(strings.title)}
        </ThemedText>

        <View style={styles.headerSpacer} />
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        <ThemedText variant="body" tone="secondary">
          {t(strings.subtitle)}
        </ThemedText>

        <View
          style={[
            styles.optionGroup,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          {options.map((option, index) => {
            const selected = preference === option.value;
            return (
              <View key={option.value}>
                {index > 0 ? (
                  <View
                    style={[styles.divider, { backgroundColor: c.border }]}
                  />
                ) : null}
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => void select(option.value)}
                  style={({ pressed }) => [
                    styles.optionRow,
                    pressed && styles.pressed,
                  ]}
                >
                  <View style={styles.optionCopy}>
                    <ThemedText variant="body">{option.label}</ThemedText>
                    {option.value === "system" ? (
                      <ThemedText variant="caption" tone="muted">
                        {t(strings.systemDefaultDescription)}
                      </ThemedText>
                    ) : null}
                  </View>
                  {selected ? (
                    <Ionicons
                      name="checkmark-circle"
                      size={22}
                      color={c.tint}
                    />
                  ) : null}
                </Pressable>
              </View>
            );
          })}
        </View>

        <ThemedText variant="caption" tone="muted">
          {t(strings.moreComing)}
        </ThemedText>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  pressed: { opacity: 0.72 },
  headerTitle: { flex: 1, textAlign: "center" },
  headerSpacer: { width: 40 },
  content: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
    gap: Spacing.lg,
    padding: Spacing.lg,
  },
  optionGroup: {
    borderWidth: 1,
    borderRadius: Radius.lg,
    overflow: "hidden",
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    minHeight: 54,
  },
  optionCopy: { flex: 1, gap: 2 },
  divider: { height: StyleSheet.hairlineWidth },
});
